/**
 * Mouvement, guidage et impacts de tous les projectiles, intégrés au tick fixe.
 * Port de AMissileProjectile, ASwarmMissileProjectile et AEnemyProjectile.
 */
import { DEG, Quat, Vec3, segmentSphere } from '../core/math';
import type { DamageSource, Projectile, Ship, WeaponKind } from '../entities';
import type { SimState } from '../state';

export interface EnemyBoltSpec {
  kind: 'bolt' | 'railgun' | 'volley' | 'special' | 'bossBolt';
  owner: Ship;
  source: DamageSource;
  origin: Vec3;
  direction: Vec3;
  speed: number;
  range: number;
  damage: number;
  color: [number, number, number];
}

/** Durée d'un bolt : portée / vitesse + 0,3 s, bornée entre 0,5 et 2,5 s (implementation_ue §7). */
export function boltLife(range: number, speed: number): number {
  return Math.max(0.5, Math.min(2.5, range / Math.max(speed, 1) + 0.3));
}

export function spawnEnemyBolt(state: SimState, spec: EnemyBoltSpec): Projectile {
  const dir = spec.direction.normalized(spec.owner.forward());
  const projectile: Projectile = {
    id: state.newId(),
    kind: spec.kind,
    team: 'enemy',
    ownerId: spec.owner.id,
    source: spec.source,
    position: spec.origin.clone(),
    velocity: dir.scale(spec.speed),
    radius: spec.kind === 'railgun' ? 0.35 : spec.kind === 'special' ? 0.8 : 0.5,
    damage: spec.damage,
    blastRadius: 0,
    life: boltLife(spec.range, spec.speed),
    age: 0,
    alive: true,
    color: spec.color,
    targetId: null,
    homingActivation: 0,
    homingActive: false,
    targetLost: false,
    offTargetSeconds: 0,
    minSpeed: spec.speed,
    maxSpeed: spec.speed,
    homingAcceleration: 0,
  };
  state.projectiles.push(projectile);
  state.emit({ type: 'projectileSpawned', time: state.time, projectileId: projectile.id, kind: projectile.kind, position: projectile.position.toArray() });
  state.emit({
    type: 'enemyFired',
    time: state.time,
    shipId: spec.owner.id,
    source: spec.source,
    kind: weaponOf(spec.kind),
    origin: spec.origin.toArray(),
    direction: dir.toArray(),
    color: spec.color,
  });
  return projectile;
}

function weaponOf(kind: Projectile['kind']): WeaponKind {
  switch (kind) {
    case 'missile': return 'missile';
    case 'swarm': return 'swarm';
    case 'railgun': return 'railgun';
    case 'volley': return 'volley';
    case 'special': return 'special';
    default: return 'bolt';
  }
}

export function updateProjectiles(state: SimState, dt: number): void {
  for (const p of state.projectiles) {
    if (!p.alive) continue;
    p.age += dt;
    p.life -= dt;
    if (p.kind === 'swarm') guideSwarm(state, p, dt);
    if (!p.alive) continue;
    if (p.kind === 'missile' || (p.kind === 'swarm' && p.homingActive && !p.targetLost)) applyHoming(state, p, dt);
    move(state, p, dt);
    if (p.alive && p.life <= 0) expire(state, p);
  }
  // Compactage.
  for (let i = state.projectiles.length - 1; i >= 0; i--) {
    if (!state.projectiles[i]!.alive) state.projectiles.splice(i, 1);
  }
}

/** Accélération de homing de UProjectileMovementComponent, puis plafond de vitesse. */
function applyHoming(state: SimState, p: Projectile, dt: number): void {
  const target = state.findShip(p.targetId);
  if (target?.alive) {
    const toTarget = target.position.sub(p.position).normalized();
    p.velocity.addScaledInPlace(toTarget, p.homingAcceleration * dt);
  }
  const speed = p.velocity.length();
  if (speed > p.maxSpeed) p.velocity.scaleInPlace(p.maxSpeed / speed);
}

function guideSwarm(state: SimState, p: Projectile, dt: number): void {
  const cfg = state.data.weapons.swarm;
  if (p.targetLost) return;
  let target = state.findShip(p.targetId);
  if (!target?.alive) {
    target = acquireReplacement(state, p);
    if (!target) {
      loseTarget(state, p);
      return;
    }
  }
  const distance = target.position.distanceTo(p.position);
  if (p.age >= cfg.homingDelay && distance <= cfg.proximityFuse) {
    state.emit({ type: 'swarmProximity', time: state.time, projectileId: p.id });
    explode(state, p, p.position);
    return;
  }
  if (!p.homingActive && p.age >= p.homingActivation) p.homingActive = true;
  if (p.homingActive) steerSwarm(state, p, target, distance, dt);
  if (!p.homingActive || p.age < cfg.lossGrace) return;
  const toTarget = target.position.sub(p.position).normalized();
  const direction = p.velocity.normalized();
  if (!direction.isNearlyZero() && direction.dot(toTarget) < cfg.lossDot) {
    p.offTargetSeconds += dt;
    if (p.offTargetSeconds >= cfg.lossDuration) loseTarget(state, p);
  } else {
    p.offTargetSeconds = 0;
  }
}

/** Correction directionnelle avec anticipation et virage terminal (SwarmMissileProjectile::UpdateGuidance). */
function steerSwarm(state: SimState, p: Projectile, target: Ship, distance: number, dt: number): void {
  const cfg = state.data.weapons.swarm;
  const current = p.velocity.normalized();
  if (current.isNearlyZero()) return;
  const speed = Math.max(p.minSpeed, Math.min(p.maxSpeed, p.velocity.length()));
  const leadTime = Math.max(0, Math.min(cfg.maxLeadTime, distance / Math.max(1, speed)));
  const aim = target.position.add(target.velocity.scale(leadTime * cfg.leadScale));
  const desired = aim.sub(p.position).normalized();
  if (desired.isNearlyZero()) return;
  const span = Math.max(1, cfg.terminalDistance - cfg.proximityFuse);
  const terminal = 1 - Math.max(0, Math.min(1, (distance - cfg.proximityFuse) / span));
  const turnRate = cfg.cruiseTurnRateDeg + (cfg.terminalTurnRateDeg - cfg.cruiseTurnRateDeg) * terminal;
  const angle = Math.acos(Math.max(-1, Math.min(1, current.dot(desired))));
  if (angle <= 1e-6) {
    p.velocity.copy(desired.scale(speed));
    return;
  }
  const blend = Math.min(1, (turnRate * DEG * dt) / angle);
  const steered = Quat.identity().slerp(Quat.between(current, desired), blend).rotate(current).normalized(current);
  p.velocity.copy(steered.scale(speed));
}

function acquireReplacement(state: SimState, p: Projectile): Ship | null {
  const cfg = state.data.weapons.swarm;
  const flight = p.velocity.normalized();
  let best: Ship | null = null;
  let bestScore = Infinity;
  for (const candidate of state.ships) {
    if (!candidate.alive || candidate.team !== 'enemy') continue;
    const to = candidate.position.sub(p.position);
    const distSq = to.lengthSq();
    if (distSq > cfg.replacementRange * cfg.replacementRange) continue;
    const alignment = flight.dot(to.normalized());
    if (alignment < cfg.replacementMinDot) continue;
    const score = distSq * (1.5 + (0.75 - 1.5) * Math.max(0, alignment));
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  if (best) {
    p.targetId = best.id;
    p.homingActive = true;
    p.offTargetSeconds = 0;
  }
  return best;
}

function loseTarget(state: SimState, p: Projectile): void {
  if (p.targetLost) return;
  p.targetLost = true;
  p.targetId = null;
  p.life = Math.min(p.life, state.data.weapons.swarm.lostLife);
  state.emit({ type: 'swarmLost', time: state.time, projectileId: p.id });
}

function move(state: SimState, p: Projectile, dt: number): void {
  const start = p.position.clone();
  const delta = p.velocity.scale(dt);
  const length = delta.length();
  if (length < 1e-9) return;
  const end = start.add(delta);
  let bestT = Infinity;
  let hitShip: Ship | null = null;
  let hitNormal: Vec3 | null = null;

  const rock = state.collision.raycast(start, delta, length + p.radius);
  if (rock) {
    bestT = Math.min(1, Math.max(0, (rock.distance - p.radius) / length));
    hitNormal = rock.normal;
  }
  for (const ship of state.ships) {
    if (!ship.alive) continue;
    // Les tirs ennemis traversent les alliés ; les projectiles joueur ignorent le lanceur.
    if (p.team === 'enemy' && ship.team !== 'player') continue;
    if (p.team === 'player' && (ship.team !== 'enemy' || ship.id === p.ownerId)) continue;
    const t = segmentSphere(start, end, ship.position, ship.radius + p.radius);
    if (t !== null && t < bestT) {
      bestT = t;
      hitShip = ship;
      hitNormal = start.add(delta.scale(t)).sub(ship.position).normalized();
    }
  }
  if (bestT === Infinity) {
    p.position.copy(end);
    return;
  }
  const point = start.add(delta.scale(bestT));
  p.position.copy(point);
  if (p.team === 'player') {
    if (p.kind === 'swarm' && hitShip && hitShip.id === p.targetId) {
      state.emit({ type: 'swarmDirectHit', time: state.time, projectileId: p.id });
    }
    // Explosion légèrement en retrait de la surface pour que la ligne de vue des dégâts ne parte pas de l'intérieur.
    explode(state, p, hitShip ? point : point.sub(delta.normalized().scale(0.3)));
    return;
  }
  // Tir ennemi : dégât ponctuel sur le joueur ou impact sur le décor.
  const normal = hitNormal ?? delta.normalized().neg();
  state.emit({
    type: 'impact',
    time: state.time,
    position: point.toArray(),
    normal: normal.toArray(),
    surface: hitShip ? 'player' : 'rock',
    heavy: p.kind === 'railgun' || p.kind === 'special',
  });
  if (hitShip) {
    const owner = state.findShip(p.ownerId);
    state.applyDamage(hitShip, p.damage, {
      sourceShip: owner,
      source: p.source,
      weapon: weaponOf(p.kind),
      position: point,
      origin: owner?.alive ? owner.position : start.sub(delta.normalized().scale(50)),
    });
  }
  remove(state, p);
}

function expire(state: SimState, p: Projectile): void {
  if (p.team === 'player') explode(state, p, p.position);
  else remove(state, p);
}

function explode(state: SimState, p: Projectile, at: Vec3): void {
  if (!p.alive) return;
  const owner = state.findShip(p.ownerId);
  const weapon = p.kind === 'swarm' ? 'swarm' : 'missile';
  state.emit({
    type: 'explosion',
    time: state.time,
    position: at.toArray(),
    radius: p.blastRadius,
    color: p.kind === 'swarm' ? [1, 0.19, 0.005] : [1, 0.14, 0.005],
    duration: p.kind === 'swarm' ? 0.46 : 0.65,
    weapon,
  });
  state.applyRadialDamage(at, p.blastRadius, p.damage, p.ownerId, { sourceShip: owner, source: p.source, weapon });
  remove(state, p);
}

function remove(state: SimState, p: Projectile): void {
  p.alive = false;
  state.emit({ type: 'projectileRemoved', time: state.time, projectileId: p.id });
}
